/**
 * A MINIMAL PDF WRITER. Enough to print one page, and nothing more.
 *
 * ## Why not a library
 *
 * jsPDF is ~350 KB and pdf-lib ~1 MB, and this app has one thing to print: a till poster, which is a
 * logo, three lines of text and a QR. That is a page of PDF operators. The working agreement here is
 * "no unnecessary dependencies", and a megabyte of general-purpose typesetting shipped to a Kenyan
 * mobile connection to draw eleven rectangles is the definition of unnecessary. It is also a supply
 * chain decision: this is a regulated wallet, and every dependency is code we ship to a participant's
 * browser and have to keep watching.
 *
 * ## What it supports, and what it deliberately does not
 *
 * Text in the three base-14 fonts (no embedding), filled rectangles and rounded rectangles, and ONE
 * image format: a 1-bit-per-pixel greyscale bitmap, hex encoded. That last one is chosen for the QR:
 * hex is lossless, needs no compressor, and a QR is exactly a bitmap of black and white. A JPEG would
 * have been smaller and would have put compression artefacts on the one thing on the page that a
 * camera has to decode.
 *
 * No unicode, no embedded fonts, no compression, no encryption, no transparency. Text is coerced to
 * ASCII, which is also why the cross-reference offsets are correct: with ASCII-only content, a
 * JavaScript string's length equals its byte length, and `xref` is a table of byte offsets. Anything
 * that broke that assumption would produce a file that opens in Chrome and fails in Acrobat, which is
 * the worst way for this to be wrong.
 *
 * Reference: PDF 1.7, ISO 32000-1, sections 7.5 (file structure), 8.4 (graphics state), 9.4 (text).
 */

export type PdfFont = 'Helvetica' | 'Helvetica-Bold' | 'Courier-Bold';

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** A 1-bit bitmap: `bits[y][x]` true means BLACK. */
export interface Bitmap {
  width: number;
  height: number;
  isBlack: (x: number, y: number) => boolean;
}

const PT_PER_MM = 72 / 25.4;

/** Millimetres to PDF points. A poster is measured with a ruler, not in typographic points. */
export const mm = (value: number) => value * PT_PER_MM;

/**
 * PDF text is bytes in a font's encoding, and these fonts are WinAnsi. Anything outside ASCII is
 * transliterated rather than dropped, so a stray typographic quote becomes a straight one instead of
 * a black diamond, and `(` `)` `\` are escaped because they delimit a PDF string.
 */
function pdfText(value: string): string {
  return value
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u00a0\u202f\u2009]/g, ' ')
    .replace(/[^\x20-\x7e]/g, '')
    .replace(/([\\()])/g, '\\$1');
}

export class PdfPage {
  private ops: string[] = [];
  private images: { name: string; object: string }[] = [];

  constructor(
    readonly width: number,
    readonly height: number,
  ) {}

  fill({ r, g, b }: Rgb): this {
    this.ops.push(`${round(r)} ${round(g)} ${round(b)} rg`);
    return this;
  }

  rect(x: number, y: number, w: number, h: number): this {
    this.ops.push(`${round(x)} ${round(y)} ${round(w)} ${round(h)} re f`);
    return this;
  }

  /** A rounded rectangle, drawn with four Bezier arcs. The brand tile is one of these. */
  roundedRect(x: number, y: number, w: number, h: number, radius: number): this {
    const r = Math.min(radius, w / 2, h / 2);
    // 0.5523 is the standard circular-arc Bezier constant.
    const k = r * 0.5523;
    const [x0, y0, x1, y1] = [x, y, x + w, y + h];
    this.ops.push(
      `${round(x0 + r)} ${round(y0)} m`,
      `${round(x1 - r)} ${round(y0)} l`,
      `${round(x1 - r + k)} ${round(y0)} ${round(x1)} ${round(y0 + r - k)} ${round(x1)} ${round(y0 + r)} c`,
      `${round(x1)} ${round(y1 - r)} l`,
      `${round(x1)} ${round(y1 - r + k)} ${round(x1 - r + k)} ${round(y1)} ${round(x1 - r)} ${round(y1)} c`,
      `${round(x0 + r)} ${round(y1)} l`,
      `${round(x0 + r - k)} ${round(y1)} ${round(x0)} ${round(y1 - r + k)} ${round(x0)} ${round(y1 - r)} c`,
      `${round(x0)} ${round(y0 + r)} l`,
      `${round(x0)} ${round(y0 + r - k)} ${round(x0 + r - k)} ${round(y0)} ${round(x0 + r)} ${round(y0)} c`,
      'f',
    );
    return this;
  }

  text(value: string, x: number, y: number, size: number, font: PdfFont = 'Helvetica'): this {
    this.ops.push(
      'BT',
      `/${fontKey(font)} ${round(size)} Tf`,
      `1 0 0 1 ${round(x)} ${round(y)} Tm`,
      `(${pdfText(value)}) Tj`,
      'ET',
    );
    return this;
  }

  /** Text centred on `cx`, using the base-14 metrics below. */
  textCentered(
    value: string,
    cx: number,
    y: number,
    size: number,
    font: PdfFont = 'Helvetica',
  ): this {
    return this.text(value, cx - textWidth(value, size, font) / 2, y, size, font);
  }

  /**
   * A 1-bit bitmap, drawn into the given box.
   *
   * Emitted as `/DeviceGray` with `/BitsPerComponent 1`, hex encoded: one bit per pixel, 1 = white,
   * rows padded to a byte boundary, which is what the spec requires and what every reader assumes.
   */
  image(bitmap: Bitmap, x: number, y: number, w: number, h: number): this {
    const name = `Im${this.images.length}`;
    const bytesPerRow = Math.ceil(bitmap.width / 8);
    const hex: string[] = [];

    for (let row = 0; row < bitmap.height; row += 1) {
      for (let byte = 0; byte < bytesPerRow; byte += 1) {
        let value = 0;
        for (let bit = 0; bit < 8; bit += 1) {
          const px = byte * 8 + bit;
          // Beyond the right edge is padding, and padding is white.
          const black = px < bitmap.width && bitmap.isBlack(px, row);
          if (!black) value |= 0x80 >> bit;
        }
        hex.push(value.toString(16).padStart(2, '0'));
      }
    }

    const data = `${hex.join('')}>`;
    this.images.push({
      name,
      object:
        `<< /Type /XObject /Subtype /Image /Width ${bitmap.width} /Height ${bitmap.height} ` +
        `/ColorSpace /DeviceGray /BitsPerComponent 1 /Filter /ASCIIHexDecode /Length ${data.length} >>\n` +
        `stream\n${data}\nendstream`,
    });

    // The image occupies the unit square, so the CTM both scales and positions it.
    this.ops.push(
      'q',
      `${round(w)} 0 0 ${round(h)} ${round(x)} ${round(y)} cm`,
      `/${name} Do`,
      'Q',
    );
    return this;
  }

  /** @internal */
  build(): { content: string; images: { name: string; object: string }[] } {
    return { content: this.ops.join('\n'), images: this.images };
  }
}

/**
 * Serialise one page to a PDF file.
 *
 * The object layout is fixed and small: catalogue, pages, page, content, three fonts, then one object
 * per image. `xref` records each object's byte offset, which is why nothing above may emit a
 * non-ASCII byte.
 */
export function renderPdf(page: PdfPage, title: string): Blob {
  const { content, images } = page.build();

  const objects: string[] = [];
  const add = (body: string) => objects.push(body);

  const imageStart = 8;
  const imageRefs = images.map((img, i) => `/${img.name} ${imageStart + i} 0 R`).join(' ');

  add('<< /Type /Catalog /Pages 2 0 R >>');
  add('<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  add(
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${round(page.width)} ${round(page.height)}] ` +
      `/Resources << /Font << /F1 5 0 R /F2 6 0 R /F3 7 0 R >> ` +
      `/XObject << ${imageRefs} >> >> /Contents 4 0 R >>`,
  );
  add(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
  add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
  add('<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold /Encoding /WinAnsiEncoding >>');
  images.forEach((img) => add(img.object));
  add(`<< /Title (${pdfText(title)}) /Producer (VASP) >>`);

  let file = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(file.length);
    file += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });

  const xref = file.length;
  file += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((offset) => {
    file += `${offset.toString().padStart(10, '0')} 00000 n \n`;
  });
  file +=
    `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R /Info ${objects.length} 0 R >>\n` +
    `startxref\n${xref}\n%%EOF\n`;

  // Latin-1 out, one byte per character, so the xref offsets computed above are the real ones.
  const bytes = new Uint8Array(file.length);
  for (let i = 0; i < file.length; i += 1) bytes[i] = file.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: 'application/pdf' });
}

function fontKey(font: PdfFont): string {
  return font === 'Helvetica' ? 'F1' : font === 'Helvetica-Bold' ? 'F2' : 'F3';
}

/**
 * Base-14 widths, in thousandths of an em.
 *
 * Only what centring needs. Courier is monospaced at 600; the two Helvetica faces are approximated
 * from their AFM averages, which is accurate enough to centre a heading and would not be accurate
 * enough to justify a paragraph. This writer does not justify paragraphs.
 */
const HELVETICA_WIDTH: Record<string, number> = { default: 556, ' ': 278, '.': 278, ',': 278 };

export function textWidth(value: string, size: number, font: PdfFont): number {
  if (font === 'Courier-Bold') return (value.length * 600 * size) / 1000;
  const bold = font === 'Helvetica-Bold';
  let total = 0;
  for (const ch of value) {
    const base = HELVETICA_WIDTH[ch] ?? (/[A-Z]/.test(ch) ? 667 : /[a-z]/.test(ch) ? 528 : 556);
    total += bold ? base * 1.05 : base;
  }
  return (total * size) / 1000;
}

function round(value: number): string {
  return (Math.round(value * 100) / 100).toString();
}
